<template>
    <div class="h-screen overflow-hidden text-white bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900">
        <!-- Header -->
        <header class="px-8 py-4 border-b bg-white/10 backdrop-blur-sm border-white/20">
            <div class="flex items-center justify-between">
                <div class="flex items-center space-x-4">
                    <div
                        class="flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-r from-cyan-400 to-blue-500">
                        <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div>
                        <h1 class="text-2xl font-bold">{{ companyName }}</h1>
                        <p class="text-sm text-blue-200">Sistem Informasi Digital</p>
                    </div>
                </div>

                <div class="text-right">
                    <div class="font-mono text-3xl font-bold">{{ currentTime }}</div>
                    <div class="text-blue-200">{{ currentDate }}</div>
                </div>
            </div>
        </header>

        <!-- Main Content -->
        <div class="flex h-screen">
            <!-- Left Side - Attendance Info -->
            <div class="w-1/2 p-8">
                <div class="h-full p-6 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/20">
                    <div class="flex items-center mb-6">
                        <div class="flex items-center justify-center w-8 h-8 mr-3 bg-green-500 rounded-full">
                            <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h2 class="text-2xl font-semibold">Informasi Kehadiran</h2>
                    </div>

                    <!-- Attendance Stats -->
                    <div class="grid grid-cols-2 gap-4 mb-8">
                        <div class="p-4 text-center border bg-green-500/20 border-green-500/30 rounded-xl">
                            <div class="text-3xl font-bold text-green-400">{{ attendanceStats.present }}</div>
                            <div class="text-sm text-green-200">Hadir</div>
                        </div>
                        <div class="p-4 text-center border bg-red-500/20 border-red-500/30 rounded-xl">
                            <div class="text-3xl font-bold text-red-400">{{ attendanceStats.absent }}</div>
                            <div class="text-sm text-red-200">Tidak Hadir</div>
                        </div>
                        <div class="p-4 text-center border bg-yellow-500/20 border-yellow-500/30 rounded-xl">
                            <div class="text-3xl font-bold text-yellow-400">{{ attendanceStats.late }}</div>
                            <div class="text-sm text-yellow-200">Terlambat</div>
                        </div>
                        <div class="p-4 text-center border bg-blue-500/20 border-blue-500/30 rounded-xl">
                            <div class="text-3xl font-bold text-blue-400">{{ attendanceStats.total }}</div>
                            <div class="text-sm text-blue-200">Total</div>
                        </div>
                    </div>

                    <!-- Recent Check-ins -->
                    <div class="p-4 bg-white/5 rounded-xl">
                        <h3 class="mb-4 text-lg font-semibold">Check-in Terbaru</h3>
                        <div class="space-y-3 overflow-hidden max-h-64">
                            <div v-for="checkin in recentCheckins" :key="checkin.id"
                                class="flex items-center justify-between py-2 border-b border-white/10 last:border-b-0">
                                <div class="flex items-center space-x-3">
                                    <div
                                        class="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500">
                                        <span class="text-xs font-semibold">{{ checkin.name.charAt(0) }}</span>
                                    </div>
                                    <div>
                                        <div class="font-medium">{{ checkin.name }}</div>
                                        <div class="text-xs text-gray-300">{{ checkin.department }}</div>
                                    </div>
                                </div>
                                <div class="text-right">
                                    <div class="font-mono text-sm">{{ checkin.time }}</div>
                                    <div class="text-xs"
                                        :class="checkin.status === 'on-time' ? 'text-green-400' : 'text-yellow-400'">
                                        {{ checkin.status === 'on-time' ? 'Tepat Waktu' : 'Terlambat' }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Side - Announcements -->
            <div class="w-1/2 p-8">
                <div class="h-full p-6 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/20">
                    <div class="flex items-center mb-6">
                        <div class="flex items-center justify-center w-8 h-8 mr-3 bg-orange-500 rounded-full">
                            <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        </div>
                        <h2 class="text-2xl font-semibold">Pengumuman</h2>
                    </div>

                    <!-- Announcements List -->
                    <div class="h-full space-y-4 overflow-hidden">
                        <transition-group name="fade" tag="div">
                            <div v-for="announcement in currentAnnouncements" :key="announcement.id"
                                class="p-4 border-l-4 bg-white/5 rounded-xl" :class="announcement.priority === 'high' ? 'border-red-500' :
                                    announcement.priority === 'medium' ? 'border-yellow-500' : 'border-blue-500'">
                                <div class="flex items-start justify-between mb-2">
                                    <h3 class="text-lg font-semibold">{{ announcement.title }}</h3>
                                    <span class="px-2 py-1 text-xs font-medium rounded-full" :class="announcement.priority === 'high' ? 'bg-red-500/20 text-red-300' :
                                        announcement.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-300' :
                                            'bg-blue-500/20 text-blue-300'">
                                        {{ announcement.priority === 'high' ? 'Penting' :
                                            announcement.priority === 'medium' ? 'Sedang' : 'Info' }}
                                    </span>
                                </div>
                                <p class="mb-3 leading-relaxed text-gray-200">{{ announcement.content }}</p>
                                <div class="flex items-center justify-between text-sm text-gray-400">
                                    <span>{{ announcement.department }}</span>
                                    <span>{{ announcement.date }}</span>
                                </div>
                            </div>
                        </transition-group>
                    </div>
                </div>
            </div>
        </div>

        <!-- Bottom Ticker -->
        <div class="fixed bottom-0 left-0 right-0 py-3 overflow-hidden bg-gradient-to-r from-cyan-600 to-blue-600">
            <div class="px-4 text-lg font-medium scroll-text whitespace-nowrap">
                {{ tickerText }}
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';

// Props (can be passed from backend if needed)
const props = defineProps([
    'attendanceData',
    'announcementData'
]);

// Reactive refs
const companyName = ref('FEB INFORMASI');
const currentTime = ref('');
const currentDate = ref('');
const currentAnnouncementIndex = ref(0);

// Intervals
let timeInterval = null;
let announcementInterval = null;

const attendanceStats = ref({
    present: 156,
    absent: 12,
    late: 8,
    total: 176
});

const recentCheckins = ref([
    { id: 1, name: 'Ahmad Fauzi', department: 'IT', time: '08:15', status: 'on-time' },
    { id: 2, name: 'Sari Indah', department: 'HR', time: '08:22', status: 'on-time' },
    { id: 3, name: 'Budi Santoso', department: 'Finance', time: '08:35', status: 'late' },
    { id: 4, name: 'Maya Putri', department: 'Marketing', time: '08:10', status: 'on-time' },
    { id: 5, name: 'Rudi Hartono', department: 'Operations', time: '08:28', status: 'on-time' },
    { id: 6, name: 'Lisa Permata', department: 'HR', time: '08:45', status: 'late' }
]);

const announcements = ref([
    {
        id: 1,
        title: 'Rapat Koordinasi Bulanan',
        content: 'Seluruh kepala divisi diharapkan menghadiri rapat koordinasi bulanan yang akan dilaksanakan pada hari Kamis, 18 September 2025 pukul 09:00 WIB di ruang rapat utama.',
        priority: 'high',
        department: 'Manajemen',
        date: '17 Sep 2025'
    },
    {
        id: 2,
        title: 'Update Sistem Keamanan',
        content: 'Sistem keamanan akan mengalami pembaruan pada hari Jumat malam. Mohon pastikan untuk logout dari semua aplikasi sebelum jam 20:00 WIB.',
        priority: 'medium',
        department: 'IT Department',
        date: '17 Sep 2025'
    },
    {
        id: 3,
        title: 'Perayaan Ulang Tahun Perusahaan',
        content: 'Dalam rangka ulang tahun perusahaan yang ke-15, akan diadakan acara perayaan pada tanggal 25 September 2025. Seluruh karyawan diundang untuk berpartisipasi.',
        priority: 'low',
        department: 'HR Department',
        date: '16 Sep 2025'
    },
    {
        id: 4,
        title: 'Pelatihan Keselamatan Kerja',
        content: 'Pelatihan wajib keselamatan kerja akan dilaksanakan untuk semua karyawan pada minggu depan. Jadwal detail akan diinformasikan melalui email.',
        priority: 'medium',
        department: 'Safety Department',
        date: '17 Sep 2025'
    }
]);

const tickerText = ref('🎉 Selamat datang di sistem informasi digital Fakultas Ekonomi dan Bisnis • 📢 Jangan lupa untuk selalu menjaga protokol kesehatan • 🔔 Untuk informasi lebih lanjut hubungi bagian administrasi • 💡 Saran dan masukan dapat disampaikan melalui kotak saran •');

// Computed properties
const currentAnnouncements = computed(() => {
    // Show 2 announcements at a time
    return announcements.value.slice(currentAnnouncementIndex.value, currentAnnouncementIndex.value + 2);
});

// Methods
const updateTime = () => {
    const now = new Date();
    currentTime.value = now.toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
    currentDate.value = now.toLocaleDateString('id-ID', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

const rotateAnnouncements = () => {
    currentAnnouncementIndex.value =
        (currentAnnouncementIndex.value + 2) % announcements.value.length;
};

// Lifecycle hooks
onMounted(() => {
    updateTime();
    timeInterval = setInterval(updateTime, 1000);
    announcementInterval = setInterval(rotateAnnouncements, 8000);
});

onUnmounted(() => {
    if (timeInterval) clearInterval(timeInterval);
    if (announcementInterval) clearInterval(announcementInterval);
});
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.5s ease-in-out;
}

.slide-enter-from {
    transform: translateX(100%);
}

.slide-leave-to {
    transform: translateX(-100%);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.pulse-animation {
    animation: pulse 2s infinite;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.7;
    }
}

.scroll-text {
    animation: scroll 30s linear infinite;
}

@keyframes scroll {
    0% {
        transform: translateX(100%);
    }

    100% {
        transform: translateX(-100%);
    }
}
</style>